import type {
  ProductSelection,
  SelectionSnapshot,
} from '@zhiyue/product-center';
import { ProductSelector } from '@zhiyue/product-center/react';
import '@zhiyue/product-center/style.css';
import { Input, InputNumber, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { ProductFormSection } from '@/modules/zy-crm/components/product-form-section';

export type ProductCenterHostConfig = {
  accessToken: string;
  memberUuid: string;
};

export type ProductCenterRow = {
  key: string;
  uuid?: string;
  offeringUuid?: string;
  offeringVersionUuid?: string;
  productName?: string;
  productCode?: string;
  productType?: string;
  productUnit?: string;
  versionNo?: string;
  standardPrice?: string;
  selectionSnapshot?: SelectionSnapshot;
  opportunityPrice?: string;
  quotePrice?: string;
  quantity?: string;
  totalPrice?: string;
  rowSort?: number;
  remark?: string;
};

export type ProductCenterSelection = Omit<ProductCenterRow, 'key' | 'uuid'> & {
  selectionSnapshot: SelectionSnapshot;
};

type ProductCenterFormSectionProps = {
  title?: React.ReactNode;
  rows: ProductCenterRow[];
  productCenter?: ProductCenterHostConfig;
  transactionPrice: 'opportunityPrice' | 'quotePrice';
  transactionPriceTitle: React.ReactNode;
  addButtonText: string;
  editingRowKey?: string;
  onProductSelected: (
    row: ProductCenterRow,
    selection: ProductCenterSelection,
  ) => void;
  onAdd: () => void;
  onBeginEdit: (row: ProductCenterRow) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDraftChange: (patch: Partial<ProductCenterRow>) => void;
  onDeleteRow: (row: ProductCenterRow) => void;
  emptyText?: React.ReactNode;
  scroll?: { x?: number | string; y?: number | string };
};

const toNumber = (value: unknown) => {
  const next = Number(value ?? 0);
  return Number.isFinite(next) ? next : 0;
};

const toAmount = (value: unknown, precision = 2) =>
  toNumber(value).toFixed(precision);

const selectionToRow = (
  selection: ProductSelection,
): ProductCenterSelection => ({
  offeringUuid: selection.offeringUuid,
  offeringVersionUuid: selection.offeringVersionUuid,
  productName: selection.name,
  productCode: selection.productCode,
  productType: selection.type,
  productUnit: selection.unit,
  versionNo: selection.versionNo || selection.versionCode,
  standardPrice: toAmount(
    selection.standardPrice?.price ??
      selection.selectionSnapshot.standardPrice?.price,
    6,
  ),
  selectionSnapshot: selection.selectionSnapshot,
});

export function ProductCenterFormSection({
  title = '产品明细',
  rows,
  productCenter,
  transactionPrice,
  transactionPriceTitle,
  addButtonText,
  editingRowKey,
  onProductSelected,
  onAdd,
  onBeginEdit,
  onSaveEdit,
  onCancelEdit,
  onDraftChange,
  onDeleteRow,
  emptyText,
  scroll,
}: ProductCenterFormSectionProps) {
  const [selectorRowKey, setSelectorRowKey] = useState<string>();

  const columns: ColumnsType<ProductCenterRow> = [
    {
      title: '产品名称',
      dataIndex: 'productName',
      width: 280,
      fixed: 'left',
      render: (_value, row) =>
        row.key === editingRowKey ? (
          <Space>
            <a
              onClick={() => setSelectorRowKey(row.key)}
              style={
                !productCenter
                  ? { pointerEvents: 'none', opacity: 0.5 }
                  : undefined
              }
            >
              {row.productName ? '更换版本' : '选择版本'}
            </a>
            {productCenter && selectorRowKey === row.key ? (
              <ProductSelector
                accessToken={productCenter.accessToken}
                memberUuid={productCenter.memberUuid}
                open
                onSelect={(selection: ProductSelection) => {
                  onProductSelected(row, selectionToRow(selection));
                  setSelectorRowKey(undefined);
                }}
                onCancel={() => setSelectorRowKey(undefined)}
              />
            ) : null}
          </Space>
        ) : (
          <span>{row.productName || '-'}</span>
        ),
    },
    {
      title: '产品编码',
      dataIndex: 'productCode',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: '类型',
      dataIndex: 'productType',
      width: 110,
      render: (value) => value || '-',
    },
    {
      title: '单位',
      dataIndex: 'productUnit',
      width: 110,
      render: (value) => value || '-',
    },
    {
      title: '版本',
      dataIndex: 'versionNo',
      width: 110,
      render: (value) => value || '-',
    },
    {
      title: '标准价',
      dataIndex: 'standardPrice',
      width: 120,
      render: (value) => toAmount(value),
    },
    {
      title: transactionPriceTitle,
      dataIndex: transactionPrice,
      width: 130,
      render: (_value, row) =>
        row.key === editingRowKey ? (
          <InputNumber
            min={0}
            precision={2}
            value={toNumber(row[transactionPrice])}
            onChange={(value) =>
              onDraftChange({ [transactionPrice]: toAmount(value) })
            }
            style={{ width: '100%' }}
          />
        ) : (
          <span>{toAmount(row[transactionPrice])}</span>
        ),
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      width: 110,
      render: (_value, row) =>
        row.key === editingRowKey ? (
          <InputNumber
            min={0}
            precision={2}
            value={toNumber(row.quantity)}
            onChange={(value) => onDraftChange({ quantity: toAmount(value) })}
            style={{ width: '100%' }}
          />
        ) : (
          <span>{toAmount(row.quantity)}</span>
        ),
    },
    {
      title: '小计',
      dataIndex: 'totalPrice',
      width: 120,
      render: (value) => toAmount(value),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 180,
      render: (_value, row) =>
        row.key === editingRowKey ? (
          <Input
            value={row.remark}
            onChange={(event) => onDraftChange({ remark: event.target.value })}
            maxLength={500}
          />
        ) : (
          <span>{row.remark || '-'}</span>
        ),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_value, row) =>
        row.key === editingRowKey ? (
          <Space size={12}>
            <a onClick={onSaveEdit}>保存</a>
            <a onClick={onCancelEdit}>取消</a>
            <a onClick={() => onDeleteRow(row)}>删除</a>
          </Space>
        ) : (
          <Space size={12}>
            <a onClick={() => onBeginEdit(row)}>编辑</a>
            <a onClick={() => onDeleteRow(row)}>删除</a>
          </Space>
        ),
    },
  ];

  return (
    <ProductFormSection<ProductCenterRow>
      title={title}
      rowKey="key"
      columns={columns}
      dataSource={rows}
      editingRowKey={editingRowKey}
      emptyText={emptyText}
      addButtonText={addButtonText}
      onAdd={onAdd}
      scroll={scroll}
    />
  );
}
